import { useRef, useState, useMemo } from 'react';

export function TicketFilterPanel({
  showCreateForm,
  canCreate,
  clients,
  uniqueStatuses,
  filters,
  onReset,
  onApply,
  onToggleCreateForm,
  onFiltersChange,
  onApplySpecific,
  isAutoTab,
  employees,
  projects = [],
  onDesignKRA
}) {
  const searchDebounceRef = useRef(null);
  const [userSearchText, setUserSearchText] = useState('');

  function closeFilterMenus() {
    document.querySelectorAll('.ticket-filter-dropdown[open]').forEach((menu) => menu.removeAttribute('open'));
  }

  const sortedEmployees = useMemo(() => {
    if (!employees || !employees.length) return [];
    return [...employees].sort((a, b) => {
      const nameA = a['Employee Name'] || a.Name || a['Employee ID'] || '';
      const nameB = b['Employee Name'] || b.Name || b['Employee ID'] || '';
      return String(nameA).localeCompare(String(nameB));
    });
  }, [employees]);

  const filteredEmployeesList = useMemo(() => {
    if (!userSearchText.trim()) return sortedEmployees;
    const q = userSearchText.trim().toLowerCase();
    return sortedEmployees.filter((emp) => {
      const id = String(emp['Employee ID'] || emp['User ID'] || emp.id || '').toLowerCase();
      const name = String(emp['Employee Name'] || emp.Name || emp.name || '').toLowerCase();
      const dept = String(emp.Department || emp.department || '').toLowerCase();
      return id.includes(q) || name.includes(q) || dept.includes(q);
    });
  }, [sortedEmployees, userSearchText]);

  const selectedClientLabel = filters.clientIds.length
    ? `${filters.clientIds.length} client${filters.clientIds.length > 1 ? 's' : ''} selected`
    : 'All Clients';
  const selectedStatusLabel = filters.statuses?.length
    ? `${filters.statuses.length} status${filters.statuses.length > 1 ? 'es' : ''} selected`
    : 'All Statuses';
  let selectedFreqLabel = 'All Frequencies';
  if (filters.frequencies?.length === 1) {
    selectedFreqLabel = filters.frequencies[0];
  } else if (filters.frequencies?.length > 1) {
    selectedFreqLabel = `${filters.frequencies.length} frequencies selected`;
  }

  const selectedEmpCount = filters.employeeIds?.length || 0;
  let selectedEmpLabel = 'All Users';
  if (selectedEmpCount === 1) {
    const target = filters.employeeIds[0];
    const matched = employees?.find((emp) => {
      const id = String(emp['Employee ID'] || emp['User ID'] || emp.id || '').trim().toLowerCase();
      const name = String(emp['Employee Name'] || emp.Name || emp.name || '').trim().toLowerCase();
      const t = String(target).trim().toLowerCase();
      return id === t || name === t;
    });
    selectedEmpLabel = matched ? (matched['Employee Name'] || matched.Name || matched['Employee ID'] || target) : target;
  } else if (selectedEmpCount > 1) {
    selectedEmpLabel = `${selectedEmpCount} users selected`;
  }
  
  const frequencyOptions = ['Daily', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '15', '20', '25', '26', '27', '28', '29', '30', '31', '2nd Fri', '4th Fri'];

  return (
    <article className="migration-panel migration-panel--full" style={{ zIndex: 10, position: 'relative' }}>
      <div className="migration-panel__row">
        <h2>Advanced Filters</h2>
        <div className="ticket-filter-actions">
          <button type="button" className="inline-action inline-action--ghost" onClick={() => { onReset(); closeFilterMenus(); }}>
            Reset
          </button>
          {canCreate ? (
            <button type="button" className="inline-action inline-action--create-ticket" onClick={onToggleCreateForm}>
              {showCreateForm ? 'Hide Form' : 'Create New Ticket'}
            </button>
          ) : null}
          {isAutoTab && onDesignKRA ? (
            <button type="button" className="inline-action inline-action--create-ticket" onClick={onDesignKRA}>
              + Design KRA
            </button>
          ) : null}
        </div>
      </div>

      <div className="ticket-filter-grid">
        <div className="dashboard-control ticket-filter-dropdown-control">
          <span>Select User</span>
          <details className="ticket-filter-dropdown">
            <summary data-ticket-filter="employees">{selectedEmpLabel}</summary>
            <div className="ticket-filter-dropdown__menu" style={{ minWidth: 'max(100%, 250px)' }}>
              {sortedEmployees.length > 6 && (
                <div style={{ padding: '2px 4px 8px 4px', position: 'sticky', top: 0, background: '#ffffff', zIndex: 2, borderBottom: '1px solid #e5e7eb', marginBottom: '4px' }}>
                  <input
                    type="text"
                    value={userSearchText}
                    onChange={(e) => setUserSearchText(e.target.value)}
                    placeholder="Search user..."
                    style={{
                      width: '100%',
                      padding: '5px 8px',
                      fontSize: '12px',
                      border: '1px solid #cfdbed',
                      borderRadius: '6px',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              )}
              <label className="ticket-filter-dropdown__option">
                <input
                  type="checkbox"
                  checked={!filters.employeeIds?.length}
                  onChange={() => {
                    const next = { ...filters, employeeIds: [] };
                    if (onApplySpecific) onApplySpecific(next);
                    else onFiltersChange(next);
                  }}
                />
                <span style={{ fontWeight: !filters.employeeIds?.length ? 600 : 400 }}>All Users</span>
              </label>
              {filteredEmployeesList.map((emp) => {
                const id = String(emp['Employee ID'] || emp['User ID'] || emp.id || '').trim();
                const name = String(emp['Employee Name'] || emp.Name || emp.name || id).trim();
                const value = id || name;
                const isChecked = Boolean(
                  filters.employeeIds?.some((item) => {
                    const s = String(item).trim().toLowerCase();
                    return (id && s === id.toLowerCase()) || (name && s === name.toLowerCase());
                  })
                );
                const label = name && id && name !== id ? `${name} (${id})` : (name || id);
                return (
                  <label className="ticket-filter-dropdown__option" key={id || name}>
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => {
                        const current = filters.employeeIds || [];
                        const nextEmployeeIds = isChecked
                          ? current.filter((item) => {
                              const s = String(item).trim().toLowerCase();
                              return (id && s !== id.toLowerCase()) && (name && s !== name.toLowerCase());
                            })
                          : [...current.filter((item) => {
                              const s = String(item).trim().toLowerCase();
                              return (id && s !== id.toLowerCase()) && (name && s !== name.toLowerCase());
                            }), value];
                        const next = {
                          ...filters,
                          employeeIds: nextEmployeeIds
                        };
                        if (onApplySpecific) onApplySpecific(next);
                        else onFiltersChange(next);
                      }}
                    />
                    <span style={{ fontWeight: isChecked ? 600 : 400 }}>{label}</span>
                  </label>
                );
              })}
            </div>
          </details>
        </div>

        {!isAutoTab && (
          <>
            <div className="dashboard-control ticket-filter-dropdown-control">
              <span>Select Clients</span>
              <details className="ticket-filter-dropdown">
                <summary data-ticket-filter="clients">{selectedClientLabel}</summary>
                <div className="ticket-filter-dropdown__menu">
                  <label className="ticket-filter-dropdown__option">
                    <input
                      type="checkbox"
                      checked={!filters.clientIds.length}
                      onChange={() => onFiltersChange((current) => ({ ...current, clientIds: [] }))}
                    />
                    <span>All Clients</span>
                  </label>
                  {clients.map((client) => {
                    const value = client.Client_Id || client['Client ID'];
                    const label = client['Client Name'] || value;
                    const checked = filters.clientIds.includes(value);
                    return (
                      <label className="ticket-filter-dropdown__option" key={value}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            const next = checked
                              ? filters.clientIds.filter((item) => item !== value)
                              : [...filters.clientIds, value];
                            onFiltersChange((current) => ({ ...current, clientIds: next }));
                          }}
                        />
                        <span>{label}</span>
                      </label>
                    );
                  })}
                </div>
              </details>
            </div>

            <div className="dashboard-control ticket-filter-dropdown-control">
              <span>Task Status</span>
              <details className="ticket-filter-dropdown">
                <summary data-ticket-filter="statuses">{selectedStatusLabel}</summary>
                <div className="ticket-filter-dropdown__menu">
                  <label className="ticket-filter-dropdown__option">
                    <input
                      type="checkbox"
                      checked={!filters.statuses.length}
                      onChange={() => onFiltersChange((current) => ({ ...current, statuses: [] }))}
                    />
                    <span>All Statuses</span>
                  </label>
                  {uniqueStatuses.map((status) => {
                    const checked = filters.statuses.includes(status);
                    return (
                      <label className="ticket-filter-dropdown__option" key={status}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => onFiltersChange((current) => ({
                            ...current,
                            statuses: checked
                              ? current.statuses.filter((item) => item !== status)
                              : [...current.statuses, status]
                          }))}
                        />
                        <span>{status}</span>
                      </label>
                    );
                  })}
                </div>
              </details>
            </div>

            {projects?.length > 0 && (
              <div className="dashboard-control ticket-filter-dropdown-control">
                <span>Select Project</span>
                <details className="ticket-filter-dropdown">
                  <summary data-ticket-filter="projects">
                    {filters.search && projects.includes(filters.search) ? `📁 ${filters.search}` : 'All Projects'}
                  </summary>
                  <div className="ticket-filter-dropdown__menu">
                    <div
                      className="ticket-filter-dropdown__option"
                      style={{
                        cursor: 'pointer',
                        fontWeight: !filters.search || !projects.includes(filters.search) ? 'bold' : 'normal'
                      }}
                      onClick={() => {
                        const next = { ...filters, search: '' };
                        if (onApplySpecific) onApplySpecific(next);
                        else onFiltersChange(next);
                        closeFilterMenus();
                      }}
                    >
                      <span>All Projects</span>
                    </div>
                    {projects.map((proj) => {
                      const checked = filters.search === proj;
                      return (
                        <div
                          className="ticket-filter-dropdown__option"
                          key={proj}
                          style={{
                            cursor: 'pointer',
                            fontWeight: checked ? 'bold' : 'normal',
                            backgroundColor: checked ? 'var(--color-primary-light, #eef2ff)' : 'transparent'
                          }}
                          onClick={() => {
                            const next = {
                              ...filters,
                              search: checked ? '' : proj
                            };
                            if (onApplySpecific) onApplySpecific(next);
                            else onFiltersChange(next);
                            closeFilterMenus();
                          }}
                        >
                          <span>📁 {proj}</span>
                        </div>
                      );
                    })}
                  </div>
                </details>
              </div>
            )}
          </>
        )}

        {isAutoTab && (
          <>
            <div className="dashboard-control ticket-filter-dropdown-control">
              <span>Frequency</span>
              <details className="ticket-filter-dropdown">
                <summary data-ticket-filter="frequencies">{selectedFreqLabel}</summary>
                <div className="ticket-filter-dropdown__menu">
                  <div
                    className="ticket-filter-dropdown__option"
                    style={{ cursor: 'pointer', fontWeight: !filters.frequencies?.length ? 'bold' : 'normal' }}
                    onClick={() => {
                      const next = { ...filters, frequencies: [] };
                      if (onApplySpecific) onApplySpecific(next);
                      else onFiltersChange(next);
                      closeFilterMenus();
                    }}
                  >
                    <span>All Frequencies</span>
                  </div>
                  {frequencyOptions.map((freq) => {
                    const checked = filters.frequencies?.includes(freq);
                    return (
                      <div
                        className="ticket-filter-dropdown__option"
                        key={freq}
                        style={{ cursor: 'pointer', fontWeight: checked ? 'bold' : 'normal', backgroundColor: checked ? 'var(--color-primary-light, #eef2ff)' : 'transparent' }}
                        onClick={() => {
                          const next = {
                            ...filters,
                            frequencies: checked ? [] : [freq]
                          };
                          if (onApplySpecific) onApplySpecific(next);
                          else onFiltersChange(next);
                          closeFilterMenus();
                        }}
                      >
                        <span>{freq}</span>
                      </div>
                    );
                  })}
                </div>
              </details>
            </div>



            {projects?.length > 0 && (
              <div className="dashboard-control ticket-filter-dropdown-control">
                <span>Select Project</span>
                <details className="ticket-filter-dropdown">
                  <summary data-ticket-filter="projects">
                    {filters.search && projects.includes(filters.search) ? filters.search : 'All Projects'}
                  </summary>
                  <div className="ticket-filter-dropdown__menu">
                    <div
                      className="ticket-filter-dropdown__option"
                      style={{
                        cursor: 'pointer',
                        fontWeight: !filters.search || !projects.includes(filters.search) ? 'bold' : 'normal'
                      }}
                      onClick={() => {
                        const next = { ...filters, search: '' };
                        if (onApplySpecific) onApplySpecific(next);
                        else onFiltersChange(next);
                        closeFilterMenus();
                      }}
                    >
                      <span>All Projects</span>
                    </div>
                    {projects.map((proj) => {
                      const checked = filters.search === proj;
                      return (
                        <div
                          className="ticket-filter-dropdown__option"
                          key={proj}
                          style={{
                            cursor: 'pointer',
                            fontWeight: checked ? 'bold' : 'normal',
                            backgroundColor: checked ? 'var(--color-primary-light, #eef2ff)' : 'transparent'
                          }}
                          onClick={() => {
                            const next = {
                              ...filters,
                              search: checked ? '' : proj
                            };
                            if (onApplySpecific) onApplySpecific(next);
                            else onFiltersChange(next);
                            closeFilterMenus();
                          }}
                        >
                          <span>📁 {proj}</span>
                        </div>
                      );
                    })}
                  </div>
                </details>
              </div>
            )}
          </>
        )}

        {!isAutoTab && (
          <>
            <label className="dashboard-control">
              <span>Search</span>
              <input
                value={filters.search}
                onChange={(event) => {
                  const value = event.target.value;
                  // Update display value immediately
                  onFiltersChange((current) => ({ ...current, search: value }));
                }}
                placeholder="Ticket, client, description..."
              />
            </label>


            <label className="dashboard-control">
              <span>Time Period</span>
              <select
                value={filters.timePeriod}
                onChange={(event) => onFiltersChange((current) => ({ ...current, timePeriod: event.target.value }))}
              >
                <option value="All Time">All Time</option>
                <option value="Today">Today</option>
                <option value="This Week">This Week</option>
                <option value="Last Week">Last Week</option>
                <option value="This Month">This Month</option>
                <option value="Last Month">Last Month</option>
              </select>
            </label>
          </>
        )}
      </div>
    </article>
  );
}
